// iter150-review — Back-button support for bottom sheets.
//
// When a sheet is open, push a history marker. When the browser/Android
// back button is tapped, popstate fires: we close the sheet instead of
// navigating away from the page.
//
// When the user closes the sheet via UI (X / swipe / close button), we
// pop our marker ourselves so one press of back always returns the user
// to the actual previous page — never the sheet.
//
// When the user picks a sheet item that navigates somewhere, call the
// returned `navigateFromSheet(to)` helper. It uses navigate(to, {
// replace: true }) so our pushed marker is replaced by the destination
// — again, one press of back always suffices.
import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";

export function useSheetBackButton(open, setOpen) {
  const closedByPopRef = useRef(false);
  const markerIdRef = useRef(0);
  const navigate = useNavigate();

  useEffect(() => {
    if (!open) return undefined;
    const id = ++markerIdRef.current;
    const marker = { __mdsSheet: id };
    try { window.history.pushState(marker, ""); } catch { /* no-op */ }

    const onPop = () => {
      closedByPopRef.current = true;
      setOpen(false);
    };
    window.addEventListener("popstate", onPop);

    return () => {
      window.removeEventListener("popstate", onPop);
      // Only pop our marker if the close wasn't triggered by popstate itself.
      if (!closedByPopRef.current) {
        try {
          const st = window.history.state;
          if (st && st.__mdsSheet === id) {
            window.history.back();
          }
        } catch { /* no-op */ }
      }
      closedByPopRef.current = false;
    };
  }, [open, setOpen]);

  // Helper: close the sheet AND navigate in one tap. We replace the
  // marker entry so the destination becomes the top of history → one
  // press of back goes to the page the user came from, not back to the
  // sheet.
  const navigateFromSheet = (to) => {
    setOpen(false);
    // Give React a tick to let the sheet cleanup fire (which pops our
    // marker) BEFORE we push the destination. If we navigate first,
    // we'd end up with marker still buried in history.
    setTimeout(() => {
      navigate(to, { replace: false });
    }, 0);
  };

  return { navigateFromSheet };
}
